import { useEffect, useMemo, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, Banknote, AlertTriangle, CircleDollarSign, RefreshCw, Loader2, Shield, Upload, CheckCircle2, X, PauseCircle, Play } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import CancelSubscriptionModal from "../components/CancelSubscriptionModal";
import PauseSubscriptionModal from "../components/PauseSubscriptionModal";

// All plan feature copy now comes from the backend (`GET /api/plans`).
// Single source of truth: /app/backend/deps.py SUBSCRIPTION_PLANS.
// `featured_plan_id` here is just a UI accent — pricing/features are server-driven.
const FEATURED_PLAN_ID = "pro";
const PLAN_DISPLAY_ORDER = ["loans_only", "starter", "pro", "enterprise", "fund_manager", "enterprise_multi"];
// PFS add-on — $20/mo when stacked on Starter or Loans Only. Pro+ already
// includes PFS natively so the checkbox is hidden there. Kept in sync with
// PFS_ADDON_MONTHLY / PFS_ADDON_ELIGIBLE_PLANS in /app/backend/deps.py.
const PFS_ADDON_MONTHLY = 20;
const PFS_ADDON_ELIGIBLE = new Set(["starter", "loans_only"]);

const TABS = [
  { id: "plans", label: "Plans & subscription" },
  { id: "payouts", label: "Payouts & disputes" },
];

export default function Billing() {
  const [tab, setTab] = useState("plans");

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-6">
          <div className="overline text-slate-500">Subscription</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
            Billing
          </h1>
        </div>

        <div className="flex gap-1 border-b border-slate-200 mb-6" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              data-testid={`billing-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "plans" ? <PlansPanel /> : <PayoutsPanel />}
      </div>
    </DashboardShell>
  );
}

// ---------- PLANS PANEL ----------
function PlansPanel() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(null);
  const [billing, setBilling] = useState("monthly"); // monthly | annual
  // PFS Add-on stacks +$20/mo onto Starter / Loans Only. Honors any existing
  // subscription that already activated the add-on so the toggle reflects
  // the user's current state on render.
  const [pfsAddon, setPfsAddon] = useState(false);

  // Pre-check the add-on box if the active subscription already has it on,
  // so re-subscribing (e.g. switching cycles) doesn't silently drop it.
  useEffect(() => {
    if (user?.pfs_addon) setPfsAddon(true);
  }, [user?.pfs_addon]);
  const [showCancel, setShowCancel] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [intent, setIntent] = useState(null);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data));
    api.get("/billing/cancel/intent").then((r) => setIntent(r.data)).catch(() => {});
  }, []);

  const refreshIntent = () => {
    api.get("/billing/cancel/intent").then((r) => setIntent(r.data)).catch(() => {});
  };

  const subscribe = async (plan_id) => {
    setBusy(plan_id);
    try {
      const { data } = await api.post("/checkout/subscription", {
        plan_id,
        billing,
        pfs_addon: PFS_ADDON_ELIGIBLE.has(plan_id) ? !!pfsAddon : false,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setBusy(null);
    }
  };

  if (!plans) return <div className="text-slate-500 text-sm">Loading plans…</div>;
  const active = user?.subscription_plan;
  const annual = billing === "annual";
  const discountPct = plans?.[Object.keys(plans)[0]]?.annual_discount_pct ?? 20;
  // Render plans in our preferred display order, falling back to backend order
  // for any plan id we don't recognize. Hide `free` (admin-only).
  const orderedIds = [
    ...PLAN_DISPLAY_ORDER.filter((id) => plans[id]),
    ...Object.keys(plans).filter((id) => id !== "free" && !PLAN_DISPLAY_ORDER.includes(id)),
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-sm text-slate-500" data-testid="current-plan-line">
          Current plan: <span className="font-bold text-slate-900">{plans[active]?.name || active || "None — select a plan below"}</span>
          {user?.pfs_addon && (
            <span
              data-testid="pfs-addon-active-badge"
              className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border bg-violet-50 text-violet-800 border-violet-200"
              title={`PFS add-on: +$${PFS_ADDON_MONTHLY}/mo. ${user?.pfs_addon_active_until ? `Active through ${new Date(user.pfs_addon_active_until).toLocaleDateString()}` : ""}`}
            >
              + PFS Add-on
            </span>
          )}
          {active && user?.subscription_billing_cycle && (() => {
            const expiresAt = user.subscription_expires ? new Date(user.subscription_expires) : null;
            // Countdown when within 60 days of renewal — a gentle reminder so
            // dormant subscriptions don't auto-charge unnoticed. Beyond 60 days
            // we just show the month/year for calm distance.
            const daysLeft = expiresAt
              ? Math.ceil((expiresAt.getTime() - Date.now()) / 86400000)
              : null;
            const isCountdown = daysLeft != null && daysLeft >= 0 && daysLeft <= 60;
            const renewLabel = isCountdown
              ? `renews in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`
              : (expiresAt
                  ? `renews ${expiresAt.toLocaleDateString(undefined, { month: "short", year: "numeric" })}`
                  : null);
            const chipCls = isCountdown
              ? (daysLeft <= 7
                  ? "bg-amber-50 text-amber-800 border-amber-200"
                  : "bg-cyan-50 text-cyan-800 border-cyan-200")
              : "bg-slate-100 text-slate-700 border-slate-200";
            return (
              <span
                className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${chipCls}`}
                data-testid="current-billing-cycle-chip"
                title={expiresAt ? `Next charge: ${expiresAt.toLocaleString()}` : ""}
              >
                · {user.subscription_billing_cycle}
                {renewLabel && <span className="opacity-80">· {renewLabel}</span>}
              </span>
            );
          })()}
        </p>
        <div className="inline-flex items-center bg-slate-100 rounded-md p-0.5" data-testid="billing-cycle-toggle" role="tablist">
          <button
            role="tab"
            aria-selected={!annual}
            data-testid="billing-cycle-monthly"
            onClick={() => setBilling("monthly")}
            className={`px-4 py-1.5 text-sm rounded ${
              !annual ? "bg-white text-slate-900 shadow-sm font-semibold" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Monthly
          </button>
          <button
            role="tab"
            aria-selected={annual}
            data-testid="billing-cycle-annual"
            onClick={() => setBilling("annual")}
            className={`px-4 py-1.5 text-sm rounded inline-flex items-center gap-2 ${
              annual ? "bg-white text-slate-900 shadow-sm font-semibold" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Annual
            <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${annual ? "bg-emerald-700 text-white" : "bg-emerald-100 text-emerald-800"}`}>
              Save {discountPct}%
            </span>
          </button>
        </div>
      </div>

      {/* Win-back coupon callout — shown when the user has an unredeemed coupon
          (issued automatically 7 days before a paused subscription resumes). */}
      {user?.winback_coupon_code && !user?.winback_coupon_redeemed_at && (() => {
        const expIso = user?.winback_coupon_expires_at;
        const exp = expIso ? new Date(expIso) : null;
        if (exp && exp.getTime() < Date.now()) return null;
        const expLabel = exp
          ? exp.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
          : null;
        const pct = user?.winback_coupon_pct || 20;
        return (
          <div
            className="mb-4 flex items-center justify-between gap-3 flex-wrap bg-emerald-50 border border-emerald-200 rounded-md px-4 py-3"
            data-testid="winback-coupon-banner"
          >
            <div className="text-sm text-emerald-900 flex items-center gap-3 flex-1 min-w-0">
              <div className="bg-emerald-600 text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded shrink-0">
                Win-back
              </div>
              <div className="min-w-0">
                <span className="font-semibold">{pct}% off your next subscription</span>{" "}
                <span className="text-emerald-800">— applied automatically at checkout.</span>{" "}
                <span className="font-mono text-xs bg-white border border-emerald-300 rounded px-1.5 py-0.5 text-emerald-700" data-testid="winback-coupon-code">
                  {user.winback_coupon_code}
                </span>
                {expLabel && (
                  <span className="text-xs text-emerald-700 ml-2">expires {expLabel}</span>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {orderedIds.map((id) => {
            const p = plans[id];
            const isActive = active === id;
            const featured = id === FEATURED_PLAN_ID;
            const features = p.features || [
              `Up to ${p.max_properties === 999 ? "unlimited" : p.max_properties} properties`,
              p.max_teammates ? `${p.max_teammates} teammate seats` : "Solo account",
            ];
            // Display price: monthly shows the standard rate; annual shows the
            // effective per-month price after the 20% discount, with the full
            // annual charge underneath. Strikethrough indicates savings.
            const displayPerMo = annual ? p.amount_annual_per_month : p.amount;
            return (
              <div
                key={id}
                data-testid={`plan-${id}`}
                className={`relative rounded-sm border p-6 flex flex-col ${
                  featured ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white"
                }`}
              >
                {featured && (
                  <div className="absolute -top-2.5 left-6 bg-amber-400 text-slate-900 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded">
                    Most popular
                  </div>
                )}
                <div className={`overline ${featured ? "text-white/60" : "text-slate-500"}`}>{p.name}</div>
                {p.tagline && (
                  <div className={`text-xs mt-1 ${featured ? "text-white/60" : "text-slate-500"}`}>{p.tagline}</div>
                )}
                <div className="mt-3 flex items-baseline gap-1" data-testid={`plan-${id}-price`}>
                  <span className="text-4xl font-bold font-mono tracking-tight">
                    ${annual ? Math.round(displayPerMo) : p.amount}
                  </span>
                  <span className={featured ? "text-white/60" : "text-slate-500"}>/mo</span>
                </div>
                {annual ? (
                  <div className={`mt-1 text-[11px] ${featured ? "text-white/70" : "text-slate-500"}`} data-testid={`plan-${id}-annual-detail`}>
                    <span className="line-through opacity-60 mr-1">${Math.round(p.amount * 12)}</span>
                    <span className="font-semibold">${Math.round(p.amount_annual)}/yr</span>
                    <span className={`ml-1 ${featured ? "text-emerald-300" : "text-emerald-700"} font-semibold`}>
                      · save ${Math.round(p.annual_savings)}
                    </span>
                  </div>
                ) : (
                  <div className={`mt-1 text-[11px] ${featured ? "text-white/70" : "text-slate-500"}`}>
                    Switch to annual to save ${Math.round(p.annual_savings)}/yr
                  </div>
                )}
                {(p.max_workspaces > 0 || p.max_teammates > 0) && (
                  <div className={`mt-2 flex flex-wrap gap-1 text-[10px] uppercase tracking-widest font-bold ${featured ? "text-white/60" : "text-slate-500"}`}>
                    {p.max_properties === 999 ? <span>Unlimited props</span> : <span>{p.max_properties} props</span>}
                    {p.max_teammates > 0 && <span>· {p.max_teammates} seats</span>}
                    {p.max_workspaces > 0 && (
                      <span>
                        · {p.max_workspaces >= 999 ? "∞" : p.max_workspaces} workspaces
                      </span>
                    )}
                  </div>
                )}
                <ul className={`mt-4 space-y-2 text-sm flex-1 ${featured ? "text-white/80" : "text-slate-600"}`}>
                  {features.map((f, idx) => (
                    <li key={`${id}-${idx}`} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${featured ? "text-white/80" : "text-slate-900"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {PFS_ADDON_ELIGIBLE.has(id) && (
                  <label
                    data-testid={`pfs-addon-${id}`}
                    className={`mt-4 mb-1 flex items-start gap-2 p-3 rounded-sm border cursor-pointer transition-colors ${
                      featured
                        ? "border-white/20 bg-white/5 hover:bg-white/10"
                        : "border-violet-200 bg-violet-50 hover:bg-violet-100"
                    }`}
                    title="Adds the Personal Financial Statement generator (PFS) — emailable PDF for your lenders."
                  >
                    <input
                      type="checkbox"
                      data-testid={`pfs-addon-${id}-checkbox`}
                      checked={!!pfsAddon}
                      onChange={(e) => setPfsAddon(e.target.checked)}
                      className="mt-0.5 accent-violet-700"
                    />
                    <div className="flex-1 text-xs">
                      <div className={`font-semibold ${featured ? "text-white" : "text-violet-900"}`}>
                        + Personal Financial Statement add-on
                      </div>
                      <div className={`${featured ? "text-white/70" : "text-violet-700"} mt-0.5`}>
                        +${PFS_ADDON_MONTHLY}/mo — unlocks the lender-ready PFS PDF + auto-synced real-estate schedule.
                      </div>
                    </div>
                  </label>
                )}
                <button
                  disabled={isActive || busy === id}
                  onClick={() => subscribe(id)}
                  data-testid={`subscribe-${id}`}
                  className={`mt-6 w-full rounded-sm py-2.5 font-semibold text-sm transition-colors ${
                    isActive
                      ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                      : featured
                      ? "bg-white text-slate-900 hover:bg-slate-100"
                      : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  {busy === id ? "Redirecting…" : isActive ? "Current plan" : (annual ? "Subscribe yearly" : "Subscribe")}
                </button>
              </div>
            );
          })}
      </div>

      {/* Paused-state banner — shows when the user has paused their subscription. */}
      {user?.subscription_status === "paused" && (
        <div
          className="mt-6 flex items-start justify-between gap-4 flex-wrap bg-amber-50 border border-amber-200 rounded-md px-4 py-3"
          data-testid="billing-paused-banner"
        >
          <div className="flex items-start gap-3 text-sm text-amber-900 flex-1 min-w-0">
            <PauseCircle className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />
            <div>
              <div className="font-semibold">Subscription paused.</div>
              <div className="text-xs text-amber-800 mt-0.5">
                {user?.subscription_resumes_at ? (
                  <>Auto-resumes on{" "}
                  <span className="font-semibold">
                    {new Date(user.subscription_resumes_at).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}
                  </span>.
                  </>
                ) : (
                  <>Your portfolio is in read-only mode until you resume.</>
                )}
                {" "}You retain read-only access during the pause.
              </div>
            </div>
          </div>
          <button
            onClick={async () => {
              setResumeBusy(true);
              try {
                await api.post("/subscription/resume");
                await refresh();
                toast.success("Subscription resumed.");
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Failed to resume subscription.");
              } finally {
                setResumeBusy(false);
              }
            }}
            disabled={resumeBusy}
            data-testid="billing-resume-cta"
            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-2 rounded-sm disabled:opacity-60"
          >
            {resumeBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            Resume now
          </button>
        </div>
      )}

      {/* Cancel-subscription footer + pending-cancellation banner */}
      {active && active !== "free" && (
        <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
          {intent?.pending_cancellation ? (
            <div
              className="flex-1 bg-rose-50 border border-rose-200 rounded-md px-4 py-3 text-sm text-rose-900"
              data-testid="billing-pending-cancellation"
            >
              <span className="font-semibold">Cancellation scheduled.</span>{" "}
              You'll keep access until{" "}
              <span className="font-semibold">
                {intent.cancellation_effective_at
                  ? new Date(intent.cancellation_effective_at).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })
                  : "the end of your billing period"}
              </span>
              .
              <button
                onClick={() => setShowCancel(true)}
                className="ml-2 underline font-semibold hover:text-rose-700"
                data-testid="billing-undo-cancel-cta"
              >
                Undo
              </button>
            </div>
          ) : (
            <div className="text-xs text-slate-500">
              Need to make changes? Update your card or invoice details from the Stripe portal.
            </div>
          )}
          {!intent?.pending_cancellation && user?.subscription_status === "active" && (
            <>
              <button
                onClick={() => setShowPause(true)}
                data-testid="billing-pause-cta"
                className="text-xs text-slate-500 hover:text-cyan-700 underline mr-3"
              >
                Pause subscription
              </button>
              <button
                onClick={() => setShowCancel(true)}
                data-testid="billing-cancel-cta"
                className="text-xs text-slate-500 hover:text-rose-700 underline"
              >
                Cancel subscription
              </button>
            </>
          )}
          {!intent?.pending_cancellation && user?.subscription_status !== "active" && user?.subscription_status !== "paused" && (
            <button
              onClick={() => setShowCancel(true)}
              data-testid="billing-cancel-cta"
              className="text-xs text-slate-500 hover:text-rose-700 underline"
            >
              Cancel subscription
            </button>
          )}
        </div>
      )}

      <CancelSubscriptionModal
        open={showCancel}
        onClose={() => setShowCancel(false)}
        onCancelled={refreshIntent}
      />

      <PauseSubscriptionModal
        open={showPause}
        onClose={() => setShowPause(false)}
        onPaused={async () => { setShowPause(false); await refresh(); }}
      />
    </div>
  );
}

// ---------- PAYOUTS & DISPUTES PANEL ----------
function PayoutsPanel() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [respond, setRespond] = useState(null); // null | event row

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/stripe-connect/events?limit=100");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load events.");
      setItems([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    if (!items) return { payouts_paid: 0, payouts_paid_amount: 0, disputes_open: 0, payouts_failed: 0 };
    let paid = 0,
      paidAmt = 0,
      disputes = 0,
      failed = 0;
    for (const e of items) {
      if (e.event_type === "payout.paid") {
        paid += 1;
        paidAmt += (e.amount || 0) / 100;
      }
      if (e.event_type === "payout.failed") failed += 1;
      if (e.event_type === "charge.dispute.created") disputes += 1;
    }
    return { payouts_paid: paid, payouts_paid_amount: paidAmt, disputes_open: disputes, payouts_failed: failed };
  }, [items]);

  if (items === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading events…
      </div>
    );
  }

  return (
    <div data-testid="payouts-panel">
      <div className="flex items-start justify-between gap-4 mb-5">
        <p className="text-sm text-slate-500 max-w-2xl">
          Payouts and dispute events streamed in real time from your connected Stripe account via webhooks.
          Connect your gateway in <a href="/dashboard/settings" className="text-slate-900 font-semibold underline">Settings</a> if you haven't yet.
        </p>
        <button
          onClick={load}
          disabled={busy}
          className="btn-outline-dark text-sm flex items-center gap-2 shrink-0"
          data-testid="payouts-refresh"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat
          testid="stat-payouts-paid"
          icon={<Banknote className="w-4 h-4 text-emerald-600" />}
          label="Payouts paid"
          value={stats.payouts_paid}
          sub={stats.payouts_paid_amount > 0 ? `$${stats.payouts_paid_amount.toLocaleString()} total` : null}
        />
        <Stat
          testid="stat-payouts-failed"
          icon={<AlertTriangle className="w-4 h-4 text-amber-600" />}
          label="Payouts failed"
          value={stats.payouts_failed}
        />
        <Stat
          testid="stat-disputes"
          icon={<AlertTriangle className="w-4 h-4 text-red-600" />}
          label="Disputes opened"
          value={stats.disputes_open}
          alert={stats.disputes_open > 0}
        />
        <Stat
          testid="stat-events-total"
          icon={<CircleDollarSign className="w-4 h-4 text-slate-600" />}
          label="Total events"
          value={items.length}
        />
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-sm p-10 text-center text-sm text-slate-500" data-testid="payouts-empty">
          No Stripe Connect events yet. As soon as your first rent payout settles or a tenant opens a dispute, you'll see it here in real time.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="payouts-table">
            <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Event</th>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status / Reason</th>
                <th className="text-left px-4 py-3">Object ID</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const isDispute = (e.event_type || "").startsWith("charge.dispute.");
                return (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`payout-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <EventBadge type={e.event_type} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">
                      {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {typeof e.amount === "number"
                        ? `$${((e.amount || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${(e.currency || "usd").toUpperCase()}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {e.reason || e.status || "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-slate-400">
                      {e.object_id || "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isDispute && e.object_id ? (
                        <button
                          onClick={() => setRespond(e)}
                          className="inline-flex items-center gap-1 text-xs font-semibold border border-red-300 text-red-700 bg-white hover:bg-red-50 px-3 py-1.5 rounded-sm transition-colors"
                          data-testid={`respond-dispute-${e.id}`}
                        >
                          <Shield className="w-3.5 h-3.5" /> Respond
                        </button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {respond && (
        <RespondToDisputeDialog
          event={respond}
          onClose={() => setRespond(null)}
          onSaved={() => {
            setRespond(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function Stat({ icon, label, value, sub, alert, testid }) {
  return (
    <div
      className={`border rounded-sm p-4 ${alert ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 overline text-slate-500">
        {icon} {label}
      </div>
      <div className="mt-2 text-3xl font-bold font-mono tracking-tight text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 font-mono mt-1">{sub}</div>}
    </div>
  );
}

function EventBadge({ type }) {
  const map = {
    "payout.paid": { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Payout paid" },
    "payout.failed": { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Payout failed" },
    "payout.created": { cls: "bg-sky-50 text-sky-700 border-sky-200", label: "Payout created" },
    "charge.dispute.created": { cls: "bg-red-50 text-red-700 border-red-200", label: "Dispute opened" },
    "charge.dispute.updated": { cls: "bg-red-50 text-red-700 border-red-200", label: "Dispute updated" },
    "charge.dispute.closed": { cls: "bg-slate-100 text-slate-700 border-slate-200", label: "Dispute closed" },
    "account.updated": { cls: "bg-violet-50 text-violet-700 border-violet-200", label: "Account updated" },
  };
  const m = map[type] || { cls: "bg-slate-100 text-slate-700 border-slate-200", label: type };
  return (
    <span className={`inline-flex items-center text-[10px] font-mono uppercase tracking-widest border rounded-sm px-2 py-1 ${m.cls}`}>
      {m.label}
    </span>
  );
}

// ---------- RESPOND TO DISPUTE DIALOG ----------
const DISPUTE_FILES = [
  { key: "signed_lease", label: "Signed lease / agreement", hint: "PDF of the executed lease showing the tenant's signature." },
  { key: "payment_history", label: "Payment history / rent ledger", hint: "Export showing that this rent payment was invoiced + paid by the tenant." },
  { key: "customer_communication_file", label: "Tenant communication", hint: "Screenshot of texts/emails where the tenant acknowledged the rent." },
  { key: "receipt_file", label: "Receipt", hint: "Receipt you issued after the payment, if applicable." },
  { key: "service_documentation", label: "Service documentation", hint: "Photos of the unit being delivered / occupied (key handover, move-in checklist)." },
  { key: "uncategorized_file", label: "Other supporting file", hint: "Anything else that backs your side of the story." },
];

function RespondToDisputeDialog({ event, onClose, onSaved }) {
  const [files, setFiles] = useState({}); // key → File
  const [text, setText] = useState({
    product_description: "",
    customer_communication_text: "",
    uncategorized_text: "",
  });
  const [submit, setSubmit] = useState(true);
  const [busy, setBusy] = useState(false);

  const setFile = (key, f) => setFiles((prev) => ({ ...prev, [key]: f || null }));

  const totalAttached = Object.values(files).filter(Boolean).length;
  const anyText = Object.values(text).some((t) => t.trim().length > 0);

  const submitEvidence = async (e) => {
    e.preventDefault();
    if (totalAttached === 0 && !anyText) {
      toast.error("Attach at least one file or fill in a text field before submitting.");
      return;
    }
    const fd = new FormData();
    Object.entries(files).forEach(([k, f]) => { if (f) fd.append(k, f); });
    Object.entries(text).forEach(([k, v]) => { if (v.trim()) fd.append(k, v.trim()); });
    fd.append("submit", submit ? "true" : "false");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/stripe-connect/disputes/${event.object_id}/evidence`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        submit
          ? `Evidence submitted. Stripe status: ${data.status || "under_review"}`
          : "Evidence saved as draft in Stripe. You can still edit until you hit Submit."
      );
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Stripe rejected the evidence.");
    } finally {
      setBusy(false);
    }
  };

  const amt = (event.amount || 0) / 100;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="respond-dispute-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-600" /> Respond to dispute
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-slate-600 bg-red-50 border border-red-200 rounded-sm p-3 mb-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-red-700 mb-1">Dispute details</div>
          <div>
            <b>${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {(event.currency || "usd").toUpperCase()}</b>
            {" · "}Reason: <b>{event.reason || "—"}</b>
            {" · "}ID: <code className="font-mono text-[10px]">{event.object_id}</code>
          </div>
          <div className="mt-1 text-slate-500">
            You have roughly 7 days from the dispute open date to submit evidence. Stripe's default dispute fee is $15 — you forfeit it plus the disputed amount if you lose.
          </div>
        </div>

        <form onSubmit={submitEvidence} className="space-y-4">
          <div>
            <div className="overline text-slate-600 mb-2">Attach evidence files</div>
            <div className="grid gap-2">
              {DISPUTE_FILES.map((f) => (
                <FileRow
                  key={f.key}
                  label={f.label}
                  hint={f.hint}
                  file={files[f.key]}
                  onChange={(file) => setFile(f.key, file)}
                  testid={`dispute-file-${f.key}`}
                />
              ))}
            </div>
          </div>

          <div>
            <div className="overline text-slate-600 mb-1">Product / service description</div>
            <textarea
              rows={2}
              maxLength={20000}
              className="input-clean w-full text-sm"
              data-testid="dispute-product-description"
              placeholder="e.g. Monthly rent for unit 4B at 123 Main St, leased to [tenant name] since [date]."
              value={text.product_description}
              onChange={(e) => setText({ ...text, product_description: e.target.value })}
            />
          </div>

          <div>
            <div className="overline text-slate-600 mb-1">Tenant communication (typed transcript)</div>
            <textarea
              rows={3}
              maxLength={20000}
              className="input-clean w-full text-sm"
              data-testid="dispute-customer-communication"
              placeholder="Paste text messages / email snippets where the tenant acknowledged the rent was owed and/or paid."
              value={text.customer_communication_text}
              onChange={(e) => setText({ ...text, customer_communication_text: e.target.value })}
            />
          </div>

          <div>
            <div className="overline text-slate-600 mb-1">Additional notes for Stripe</div>
            <textarea
              rows={3}
              maxLength={20000}
              className="input-clean w-full text-sm"
              data-testid="dispute-uncategorized-text"
              placeholder="Anything else Stripe should know to rule in your favor."
              value={text.uncategorized_text}
              onChange={(e) => setText({ ...text, uncategorized_text: e.target.value })}
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-sm p-3">
            <input
              type="checkbox"
              checked={submit}
              onChange={(e) => setSubmit(e.target.checked)}
              className="mt-0.5"
              data-testid="dispute-submit-checkbox"
            />
            <span>
              <b>Submit to Stripe now</b> — once submitted, evidence is final and can't be edited. Uncheck to save as a draft in Stripe that you can keep editing in the Stripe dashboard.
            </span>
          </label>

          <DialogFooter>
            <button type="button" className="btn-outline-dark" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              disabled={busy || (totalAttached === 0 && !anyText)}
              className="btn-primary-dark flex items-center gap-2"
              data-testid="dispute-submit-evidence"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {submit ? "Submit evidence to Stripe" : "Save draft in Stripe"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FileRow({ label, hint, file, onChange, testid }) {
  return (
    <label
      className={`border rounded-sm px-3 py-2.5 flex items-center gap-3 cursor-pointer transition-colors ${
        file ? "border-emerald-300 bg-emerald-50/40" : "border-dashed border-slate-300 hover:border-slate-500 hover:bg-slate-50"
      }`}
      data-testid={testid}
    >
      <div className={`w-8 h-8 flex items-center justify-center rounded-sm shrink-0 ${file ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"}`}>
        <Upload className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 truncate">{label}</div>
        <div className="text-[11px] text-slate-500 leading-snug">
          {file ? <span className="text-emerald-700 font-mono">{file.name} · {(file.size / 1024).toFixed(0)} KB</span> : hint}
        </div>
      </div>
      {file ? (
        <button
          type="button"
          className="text-slate-400 hover:text-red-600 p-1 shrink-0"
          onClick={(e) => { e.preventDefault(); onChange(null); }}
          title="Remove"
        >
          <X className="w-4 h-4" />
        </button>
      ) : (
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 shrink-0">Add</span>
      )}
      <input
        type="file"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
    </label>
  );
}
